import { ListingType, Transaction, orUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { Flex, Tooltip, Typography } from '@aloft/toolkit-sui';
import { UseQueryResult } from '@tanstack/react-query';

import { ListingResult } from '../../../controller/hooks/listings/types.ts';
import { propertyIdToReferenceViewId } from '../../../modules/referenceView/index.ts';
import {
  setActiveID,
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../../redux/features/referenceBarUISlice.ts';
import { useAppDispatch } from '../../../redux/hooks.ts';

type ValuationTablePropertyHeaderPriceProps = {
  propertyId: string;
  hideActions: boolean;
  isSubject: boolean;
  listing: UseQueryResult<ListingResult>;
  transaction: Transaction;
  isFeatureGrid?: boolean;
  listingType?: ListingType;
};

export const ValuationTablePropertyHeaderPrice = ({
  propertyId,
  hideActions,
  isSubject,
  listing,
  transaction,
  isFeatureGrid = false,
  listingType,
}: ValuationTablePropertyHeaderPriceProps) => {
  const dispatch = useAppDispatch();
  const isRentalComparables = listingType === 'rentalListing';

  const { price, status, rentRate } = transaction;
  const salePrice = orUndefined((value) => schAloftPrice.parse(value))(price);
  const rentalRate = orUndefined((value) => schAloftPrice.parse(value))(rentRate);

  const hasListing = listing.data?.status === 'found';

  let priceType = 'Listing Price';
  if (isRentalComparables) {
    priceType = 'Rent Rate';
  } else if (isSubject) {
    priceType = 'Prior Sale Price';
  } else if (status === 'SettledSale') {
    priceType = 'Sale Price';
  }

  const handleHeaderTextClick = () => {
    if (hideActions) return;

    dispatch(setActiveID(propertyIdToReferenceViewId(propertyId)));
    dispatch(setSelectedDataSheet(hasListing ? 'mls-listing' : 'public-record'));
    dispatch(setReferenceViewOpen(true));
  };

  const shouldDisplayPrice = !(isSubject && status !== 'SettledSale') && !(isSubject && isFeatureGrid);

  return (
    <Tooltip
      openDelay={500}
      label={hasListing ? 'View Listing' : 'View Public Record'}
      isDisabled={hideActions}
    >
      <Flex
        alignItems="center"
        gap="4px"
        cursor={hideActions ? 'default' : 'pointer'}
        onClick={handleHeaderTextClick}
      >
        {shouldDisplayPrice && (
          <>
            <Typography variant="h4">{`${priceType}: `}</Typography>
            <Typography variant="h4">
              {isRentalComparables
                ? (rentalRate?.toString(true) ?? '-')
                : (salePrice?.toString(true) ?? '-')}
            </Typography>
          </>
        )}
        <Flex flexGrow={1} justifyContent="flex-end" />
      </Flex>
    </Tooltip>
  );
};
